import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useEffect, useState } from "react"
import { X, Save } from "lucide-react"
import { api } from "@/lib/api"
import { cn } from "@/lib/utils"
import { vorgangBezeichnung } from "@/lib/vorgangName"
import { aufgabeKennung } from "@/lib/problemKontext"
import { HeroContactPicker, HeroProjectPicker } from "./TaskCreateModal"

type Assignee = { user_id: number; name: string }

/**
 * Task Edit-Modal — 1:1-Port von /admin/tasks/{id}/edit (altes Portal-HTML).
 * Nutzt form-urlencoded POST an /admin/tasks/{id}/edit.
 */
export function TaskEditModal({
  open, onClose, task,
}: {
  open: boolean
  onClose: () => void
  task: {
    id: number
    /** #232: nur für „Problem melden“ — bearbeitet wird über die lokale `id`. */
    hero_task_id?: number | string | null
    title: string
    comment?: string | null
    due_date?: string | null
    tenant_slug?: string | null
    kunden_slug?: string | null
    target_user_id?: number | null
    target_user_name?: string | null
    contact_id?: string | number | null
    project_match_id?: string | number | null
    project_nr?: string | null
    project_title?: string | null
  } | null
}) {
  const qc = useQueryClient()
  const [title, setTitle] = useState("")
  const [comment, setComment] = useState("")
  const [dueDate, setDueDate] = useState("")
  const [tenant, setTenant] = useState("")
  const [kunde, setKunde] = useState("")
  const [assignee, setAssignee] = useState("")
  const [contact, setContact] = useState<{ id: number; name: string } | null>(null)
  const [project, setProject] = useState<{ id: number; name: string } | null>(null)
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => {
    if (open && task) {
      setTitle(task.title || "")
      setComment(task.comment || "")
      setDueDate((task.due_date || "").slice(0, 10))
      setTenant(task.tenant_slug || "")
      setKunde(task.kunden_slug || "")
      // 🔴 Die ID allein reicht. Vorher verlangte die Bedingung ID UND Name —
      // fehlte einer, stand das Feld auf „kein Mitarbeiter", obwohl einer
      // zugewiesen war. Auf Prod haben 335 Aufgaben eine ID, aber nur 333
      // einen Namen; das Dossier lieferte lange gar keine ID.
      setAssignee(
        task.target_user_id
          ? `${task.target_user_id}|${task.target_user_name || ""}`
          : "")
      setContact(task.contact_id ? { id: Number(task.contact_id), name: `Kontakt #${task.contact_id}` } : null)
      // #98: nie „Projekt #7284194" als Name erfinden. Leer = die Auswahl
      // schlaegt die Bezeichnung in HERO nach.
      setProject(task.project_match_id ? { id: Number(task.project_match_id), name: vorgangBezeichnung(task.project_nr, task.project_title) || "" } : null)
      setErr(null)
    }
  }, [open, task])

  const tenantsDd = useQuery({
    queryKey: ["tenants-dropdown"],
    queryFn: async () => (await api.GET("/api/v1/tenants", {})).data,
    staleTime: 1000 * 60 * 5,
    enabled: open,
  })

  const assignees = useQuery<{ monteure: Assignee[]; hinweis?: string | null }>({
    queryKey: ["task-assignees", tenant],
    queryFn: async () => {
      const res = await fetch(`/api/v1/tasks/assignees?tenant=${encodeURIComponent(tenant)}`, { credentials: "include" })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      return res.json()
    },
    enabled: open && !!tenant,
  })

  const save = useMutation({
    mutationFn: async () => {
      if (!task) return
      const res = await fetch(`/api/v1/tasks/${task.id}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title, comment, due_date: dueDate,
          tenant_slug: tenant, kunden_slug: kunde,
          target_user: assignee,
          contact_id: contact?.id ? String(contact.id) : "",
          project_match_id: project?.id ? String(project.id) : "",
        }),
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tasks"] })
      onClose()
    },
    onError: (e) => setErr(e instanceof Error ? e.message : String(e)),
  })

  if (!open || !task) return null

  return (
    <div
      className="pp-overlay"
      data-portal-modal
      data-kontext-popup="Aufgabe bearbeiten"
      data-kontext-aufgabe-id={aufgabeKennung(task.hero_task_id, task.id)}
      style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.5)",
               display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
      onMouseDown={(ev) => { if (ev.target === ev.currentTarget) onClose() }}
    >
      <form
        onSubmit={(ev) => { ev.preventDefault(); setErr(null); save.mutate() }}
        onMouseDown={(e) => e.stopPropagation()}
        style={{
          background: "var(--bg-card)", borderRadius: 12, boxShadow: "0 20px 60px rgba(0,0,0,.25)",
          width: "100%", maxWidth: 520, maxHeight: "90vh", overflow: "hidden",
          display: "flex", flexDirection: "column",
        }}
      >
        <div className="px-5 py-3 border-b flex items-center justify-between">
          <h2 className="text-[15px] font-semibold">Aufgabe bearbeiten</h2>
          <button type="button" onClick={onClose}><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 overflow-y-auto space-y-3">
          {err && <div className="pp-alert pp-alert-err">{err}</div>}

          <div>
            <label className="pp-form-label">Titel *</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} required />
          </div>
          <div>
            <label className="pp-form-label">Beschreibung / Kommentar</label>
            <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={4} />
          </div>
          <div>
            <label className="pp-form-label">Fälligkeitsdatum</label>
            <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
          </div>
          <div>
            <label className="pp-form-label">Tenant</label>
            <select value={tenant} onChange={(e) => setTenant(e.target.value)}>
              <option value="">– kein Tenant –</option>
              {tenantsDd.data?.items?.map((t) => (
                <option key={t.slug} value={t.slug}>{t.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="pp-form-label">Mitarbeiter</label>
            <select value={assignee} onChange={(e) => setAssignee(e.target.value)} disabled={!tenant}>
              <option value="">– kein Mitarbeiter –</option>
              {/* 🔴 Eigener Eintrag fuer den ZUGEWIESENEN, wenn er nicht in der
                  geladenen Liste steht. Ein <select> ohne passende <option>
                  faellt stumm auf den Platzhalter zurueck — es sieht dann aus,
                  als sei niemand zugewiesen. Das trifft zwei Faelle: die Liste
                  ist noch nicht geladen, und der Mitarbeiter ist gar nicht
                  mehr dabei (ausgeschieden, anderer Mandant). */}
              {assignee && !assignees.data?.monteure?.some(
                (m) => `${m.user_id}|${m.name}` === assignee) && (
                <option value={assignee}>
                  {assignee.split("|")[1] || `Mitarbeiter #${assignee.split("|")[0]}`}
                  {assignees.isLoading ? "" : " (nicht in der Liste)"}
                </option>
              )}
              {assignees.data?.monteure?.map((m) => (
                <option key={m.user_id} value={`${m.user_id}|${m.name}`}>{m.name}</option>
              ))}
            </select>
            {assignees.data && !assignees.data.monteure?.length && (
              <div className="text-xs" data-testid="keine-mitarbeiter" style={{ marginTop: 4, color: "var(--text-muted, #6b7280)" }}>
                {assignees.data.hinweis || "Keine Mitarbeiter hinterlegt."}
              </div>
            )}
            {assignees.isError && (
              <div className="text-xs" style={{ marginTop: 4, color: "#b91c1c" }}>Mitarbeiterliste konnte nicht geladen werden.</div>
            )}
          </div>

          <HeroContactPicker tenant={tenant} value={contact} onChange={setContact} disabled={!!project} />
          <HeroProjectPicker tenant={tenant} contactId={contact?.id} value={project} onChange={setProject} disabled={!!contact} />
        </div>
        <div className="px-5 py-3 border-t flex justify-end gap-2">
          <button type="button" onClick={onClose} className="pp-btn pp-btn-sm">Abbrechen</button>
          <button
            type="submit"
            disabled={!title || save.isPending}
            className={cn("pp-btn pp-btn-primary pp-btn-sm", (!title || save.isPending) && "opacity-50 cursor-not-allowed")}
          >
            <Save className="w-3 h-3" /> {save.isPending ? "Speichert..." : "Speichern"}
          </button>
        </div>
      </form>
    </div>
  )
}
